import React, { useRef } from 'react';
import { useInView } from '../hooks';

const checks = [
  { title: 'Fully vetted & insured cleaners', desc: '— reliable professionals you can trust in your home.' },
  { title: 'Eco-friendly products', desc: '— safe for families, children, and pets.' },
  { title: 'Flexible scheduling', desc: '— same-day, weekly, and one-off cleans available.' },
  { title: 'Airbnb specialists', desc: '— fast turnovers with hotel-standard results.' },
];

export default function About() {
  const ref = useRef(null);
  const inView = useInView(ref);

  return (
    <section id="about" ref={ref} style={{
      padding: '100px 5%',
      background: 'var(--cream)',
      display: 'flex', alignItems: 'center', gap: 80, flexWrap: 'wrap',
    }}>
      <style>{`
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-40px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(40px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        .about-img-col.visible { animation: slideInLeft 0.8s ease both; }
        .about-text-col.visible { animation: slideInRight 0.8s 0.15s ease both; }
        @media (max-width: 768px) {
          #about { flex-direction: column !important; }
          .about-img-col { max-width: 100% !important; }
        }
      `}</style>

      {/* IMAGE COLUMN */}
      <div
        className={`about-img-col${inView ? ' visible' : ''}`}
        style={{ flex: 1, minWidth: 280, maxWidth: 480, position: 'relative', opacity: inView ? 1 : 0 }}
      >
        {/* Save your photo as: public/images/about.jpg */}
        <div style={{
          borderRadius: 24, overflow: 'hidden',
          boxShadow: '12px 12px 0 var(--orange)',
          background: 'linear-gradient(135deg, var(--blue) 0%, var(--sky) 100%)',
          height: 420,
        }}>
          <img
            src="/images/about.jpg"
            alt="SE1 HostCare professional cleaning team"
            onError={e => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
          />
          {/* Fallback emoji if image missing */}
          <div style={{
            display: 'none', width: '100%', height: '100%',
            alignItems: 'center', justifyContent: 'center', fontSize: 100,
            background: 'linear-gradient(135deg, var(--blue) 0%, var(--sky) 100%)',
          }}>🧹</div>
        </div>

        {/* 5 star badge */}
        <div style={{
          position: 'absolute', bottom: -10, right: -10,
          background: 'var(--navy)', color: '#fff',
          borderRadius: 14, padding: '16px 22px', textAlign: 'center',
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
        }}>
          <strong style={{ fontSize: '1.8rem', fontFamily: "'Playfair Display', serif", display: 'block', color: 'var(--orange)' }}>5★</strong>
          <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>Rated Service</span>
        </div>

        {/* Years experience badge */}
        <div style={{
          position: 'absolute', top: 20, left: -16,
          background: 'var(--orange)', color: '#fff',
          borderRadius: 14, padding: '12px 18px', textAlign: 'center',
          boxShadow: '0 8px 24px rgba(245,140,30,0.35)',
        }}>
          <strong style={{ fontSize: '1.4rem', fontFamily: "'Playfair Display', serif", display: 'block' }}>5+</strong>
          <span style={{ fontSize: '0.7rem', opacity: 0.9 }}>Years Experience</span>
        </div>
      </div>

      {/* TEXT COLUMN */}
      <div
        className={`about-text-col${inView ? ' visible' : ''}`}
        style={{ flex: 1, minWidth: 280, opacity: inView ? 1 : 0 }}
      >
        <div style={{ fontSize: '0.78rem', fontWeight: 600, letterSpacing: '2.5px', textTransform: 'uppercase', color: 'var(--orange)', marginBottom: 12 }}>
          About Us
        </div>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.7rem, 3.5vw, 2.4rem)', color: 'var(--navy)', lineHeight: 1.25, marginBottom: 18 }}>
          Experienced &amp; Reliable Cleaners in the Heart of London
        </h2>
        <p style={{ color: 'var(--muted)', lineHeight: 1.75, marginBottom: 28, fontSize: '0.97rem' }}>
          We are SE1 HostCare Cleaning — a dedicated team of professional cleaners serving homes, Airbnb properties, and rentals across the SE1 area. We take pride in delivering spotless results every single time, with flexible scheduling built around your lifestyle.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {checks.map(({ title, desc }) => (
            <div key={title} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{
                width: 28, height: 28, minWidth: 28,
                background: 'rgba(245,140,30,0.12)', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--orange)', fontSize: 14, marginTop: 1,
              }}>✓</div>
              <span style={{ fontSize: '0.92rem', color: 'var(--text)', lineHeight: 1.5 }}>
                <strong>{title}</strong> {desc}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
